import moment, { Moment } from "moment";
import React, { useEffect, useState } from "react";

import { TimeRange } from "veneer/DateTimeRangePicker";

import {
  DATA_TABLE_DATE_RANGE_PRESETS,
  DataTableDateRangeFilter,
  DataTableFilterContainer,
  DataTableMultiSelectFilter,
} from "cortex/ui/src/ui/filter/DataTableFilters";
import { Option } from "cortex/ui/src/ui/table/types";

import { INCIDENT_STATUS_OPTIONS } from "./IncidentStatus";

export type IncidentFilterId =
  | "name"
  | "device"
  | "firstOccurrence"
  | "lastNotified"
  | "notified"
  | "priority"
  | "status";

export type IncidentDatePreset =
  | "lastHour"
  | "last24Hours"
  | "last7Days"
  | "last30Days";

export interface IncidentTableFilterState {
  device: ReadonlyArray<string>;
  firstOccurrence: TimeRange;
  firstOccurrencePreset: IncidentDatePreset | null;
  lastNotified: TimeRange;
  lastNotifiedPreset: IncidentDatePreset | null;
  name: ReadonlyArray<string>;
  notified: ReadonlyArray<string>;
  priority: ReadonlyArray<string>;
  visibleFilters: ReadonlyArray<IncidentFilterId>;
  status: ReadonlyArray<string>;
}

const FILTER_IDS: ReadonlyArray<IncidentFilterId> = [
  "device",
  "firstOccurrence",
  "lastNotified",
  "name",
  "notified",
  "priority",
  "status",
];
const INCIDENT_STATUS_VALUES = new Set(
  INCIDENT_STATUS_OPTIONS.map(({ value }) => value),
);

export const INCIDENT_FILTER_STORAGE_KEY = "cortex_table_triggered_alerts";

export const getIncidentFilterStorageKey = (accountId: number): string =>
  `${INCIDENT_FILTER_STORAGE_KEY}_acct_${accountId}`;

export const getIncidentTableSettingsKey = (accountId: number): string =>
  `triggered-alerts_acct_${accountId}`;

export const createDefaultIncidentTableFilters =
  (): IncidentTableFilterState => ({
    device: [],
    firstOccurrence: [null, null],
    firstOccurrencePreset: null,
    lastNotified: [null, null],
    lastNotifiedPreset: null,
    name: [],
    notified: [],
    priority: [],
    visibleFilters: [],
    status: [],
  });

const parseStringArray = (value: unknown): ReadonlyArray<string> =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

const parseDate = (value: unknown): Moment | null => {
  if (moment.isMoment(value)) {
    return value.isValid() ? value.clone() : null;
  }

  if (typeof value !== "string") {
    return null;
  }

  const date = moment(value);
  return date.isValid() ? date : null;
};

const parseTimeRange = (value: unknown): TimeRange => {
  if (!Array.isArray(value)) {
    return [null, null];
  }

  return [parseDate(value[0]), parseDate(value[1])];
};

const parseIncidentDatePreset = (value: unknown): IncidentDatePreset | null => {
  if (
    value === "lastHour" ||
    value === "last24Hours" ||
    value === "last7Days" ||
    value === "last30Days"
  ) {
    return value;
  }

  return null;
};

const normalizeDateFilter = (
  rangeValue: unknown,
  presetValue: unknown,
): { range: TimeRange; preset: IncidentDatePreset | null } => {
  const preset = parseIncidentDatePreset(presetValue);
  const presetOption = DATA_TABLE_DATE_RANGE_PRESETS.find(
    ({ id }) => id === preset,
  );

  return preset && presetOption
    ? { preset, range: presetOption.getValue({ isValid: () => true }) }
    : { preset: null, range: parseTimeRange(rangeValue) };
};

export const normalizeIncidentTableFilters = (
  value: unknown,
): IncidentTableFilterState => {
  if (!value || typeof value !== "object") {
    return createDefaultIncidentTableFilters();
  }

  const filters = value as Record<string, unknown>;
  const visibleFilters = parseStringArray(filters.visibleFilters).filter(
    (id): id is IncidentFilterId => FILTER_IDS.includes(id as IncidentFilterId),
  );
  const firstOccurrence = normalizeDateFilter(
    filters.firstOccurrence,
    filters.firstOccurrencePreset,
  );
  const lastNotified = normalizeDateFilter(
    filters.lastNotified,
    filters.lastNotifiedPreset,
  );

  return {
    device: parseStringArray(filters.device),
    firstOccurrence: firstOccurrence.range,
    firstOccurrencePreset: firstOccurrence.preset,
    lastNotified: lastNotified.range,
    lastNotifiedPreset: lastNotified.preset,
    name: parseStringArray(filters.name),
    notified: parseStringArray(filters.notified),
    priority: parseStringArray(filters.priority),
    status: parseStringArray(filters.status).filter((status) =>
      INCIDENT_STATUS_VALUES.has(
        status as (typeof INCIDENT_STATUS_OPTIONS)[number]["value"],
      ),
    ),
    visibleFilters,
  };
};

const loadPersistedFilters = (accountId: number): IncidentTableFilterState => {
  const saved = sessionStorage.getItem(getIncidentFilterStorageKey(accountId));
  if (!saved) {
    return createDefaultIncidentTableFilters();
  }

  try {
    const parsed = JSON.parse(saved) as { filters?: unknown };
    return normalizeIncidentTableFilters(parsed.filters);
  } catch {
    return createDefaultIncidentTableFilters();
  }
};

export function usePersistedIncidentTableFilters(
  enabled: boolean,
  accountId: number,
) {
  const [filters, setFilters] = useState<IncidentTableFilterState>(() =>
    enabled
      ? loadPersistedFilters(accountId)
      : createDefaultIncidentTableFilters(),
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    sessionStorage.setItem(
      getIncidentFilterStorageKey(accountId),
      JSON.stringify({ filters }),
    );
  }, [accountId, enabled, filters]);

  return { filters, setFilters };
}

interface IncidentsTableFiltersProps {
  deviceOptions: ReadonlyArray<Option<string>>;
  nameOptions: ReadonlyArray<Option<string>>;
  notifiedOptions: ReadonlyArray<Option<string>>;
  priorityOptions: ReadonlyArray<Option<string>>;
  onChange: (value: IncidentTableFilterState) => void;
  value: IncidentTableFilterState;
}

const getSelectedOptions = (
  selectedValues: ReadonlyArray<string>,
  options: ReadonlyArray<Option<string>>,
): ReadonlyArray<Option<string>> =>
  selectedValues.map(
    (selectedValue) =>
      options.find(({ value }) => value === selectedValue) ?? {
        label: selectedValue,
        value: selectedValue,
      },
  );

export default function IncidentsTableFilters({
  deviceOptions,
  nameOptions,
  notifiedOptions,
  priorityOptions,
  onChange,
  value,
}: IncidentsTableFiltersProps) {
  const updateValues = (
    key: "device" | "name" | "notified" | "priority" | "status",
    options: ReadonlyArray<Option<string>> | null,
  ) => {
    onChange({
      ...value,
      [key]: options?.map(({ value: optionValue }) => optionValue) ?? [],
    });
  };

  const updateDateRange = (
    key: "firstOccurrence" | "lastNotified",
    range: TimeRange,
    preset: IncidentDatePreset | null,
  ) => {
    const presetKey =
      key === "firstOccurrence"
        ? "firstOccurrencePreset"
        : "lastNotifiedPreset";
    onChange({ ...value, [key]: range, [presetKey]: preset });
  };

  const renderMultiSelect = (
    id: "device" | "name" | "notified" | "priority" | "status",
    label: string,
    options: ReadonlyArray<Option<string>>,
  ) => (
    <DataTableMultiSelectFilter
      dataTestId={`triggered-alerts-filter-${id}`}
      isOptionEqualToValue={(
        option: Option<string>,
        selected: Option<string>,
      ) => option.value === selected.value}
      label={label}
      onChange={(selected) => updateValues(id, selected)}
      options={options}
      value={getSelectedOptions(value[id], options)}
    />
  );

  return (
    <DataTableFilterContainer>
      {value.visibleFilters.includes("priority") &&
        renderMultiSelect("priority", "Priority", priorityOptions)}
      {value.visibleFilters.includes("status") &&
        renderMultiSelect("status", "Status", INCIDENT_STATUS_OPTIONS)}
      {value.visibleFilters.includes("name") &&
        renderMultiSelect("name", "Name", nameOptions)}
      {value.visibleFilters.includes("device") &&
        renderMultiSelect("device", "Device", deviceOptions)}
      {value.visibleFilters.includes("firstOccurrence") && (
        <DataTableDateRangeFilter
          dataTestId="triggered-alerts-filter-first-occurrence"
          label="First Occurrence"
          onChange={(range) => updateDateRange("firstOccurrence", range, null)}
          onPresetChange={(range, preset) =>
            updateDateRange(
              "firstOccurrence",
              range,
              parseIncidentDatePreset(preset.id),
            )
          }
          selectedPresetId={value.firstOccurrencePreset}
          value={value.firstOccurrence}
        />
      )}
      {value.visibleFilters.includes("lastNotified") && (
        <DataTableDateRangeFilter
          dataTestId="triggered-alerts-filter-last-notified"
          label="Last Notified"
          onChange={(range) => updateDateRange("lastNotified", range, null)}
          onPresetChange={(range, preset) =>
            updateDateRange(
              "lastNotified",
              range,
              parseIncidentDatePreset(preset.id),
            )
          }
          selectedPresetId={value.lastNotifiedPreset}
          value={value.lastNotified}
        />
      )}
      {value.visibleFilters.includes("notified") &&
        renderMultiSelect("notified", "Notified", notifiedOptions)}
    </DataTableFilterContainer>
  );
}

export function isWithinDateRange(
  date: string | undefined,
  [start, end]: [Moment | null, Moment | null],
): boolean {
  if (!start && !end) {
    return true;
  }

  if (!date) {
    return false;
  }

  const value = moment.utc(date);
  return (!start || !value.isBefore(start)) && (!end || !value.isAfter(end));
}
